import {
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useDisclosure,
  useToast,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Skeleton,
  Tag,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { Delete, Get, Post, Put } from "api";
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { AccountManager } from "views/admin/partnerDetail/types";
import { HiDotsHorizontal, HiDotsVertical } from "react-icons/hi";
import useAdmin from "hooks/useAdmin";
import { useConfirmDialog } from "components/confirmDialog";
import {
  hideGlobalLoading,
  showGlobalLoading,
} from "components/loading/GlobalLoading";

interface AccountManagerViewProps {
  managers: AccountManager[];
  partnerId: string | number;
  partnerDataLoading: boolean;
  setPartnerData: React.Dispatch<React.SetStateAction<any>>;
}

const AccountManagerView = (props: AccountManagerViewProps) => {
  const { managers, partnerId, partnerDataLoading, setPartnerData } = props;
  const isAdmin = useAdmin();
  const mail = localStorage.getItem("mail");

  const canViewPrimaryStatus = useMemo(() => {
    if (!managers) return false;
    if (isAdmin) return true;

    return managers.some((manager) => manager.mail === mail);
  }, [managers, isAdmin, mail]);

  const canSelectAnAMAsPrimary = useMemo(() => {
    if (!managers) return false;

    return !managers.find((manager) => manager.isPrimary);
  }, [managers]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const toast = useToast();

  const {
    ConfirmDialog: ConfirmOverrideDialog,
    openDialog: openOverrideDialog,
  } = useConfirmDialog({
    title: "Overriding Primary Account Manager",
    description:
      "You are about to override the existing primary account manager with the new manager you've selected. Are you sure you want to proceed? Old primary account manager will be unmarked as primary.",
  });

  const [loadingManagers, setLoadingManagers] = useState(false);
  const [availableManagers, setAvailableManagers] = useState<AccountManager[]>(
    []
  );

  const [selectedManagerID, setSelectedManagerID] = useState<string>("");
  const [assigningManager, setAssigningManager] = useState(false);

  const handleCancel = useCallback(() => {
    onClose();
    setSelectedManagerID("");
  }, [onClose]);

  const handleCancelEditModal = useCallback(() => {
    onEditClose();
  }, [onEditClose]);

  const handleAccept = useCallback(async () => {
    if (!selectedManagerID) return;

    try {
      setAssigningManager(true);
      const response = await Post(
        `/manage/partners/${partnerId}/account-managers/${selectedManagerID}/association`,
        {}
      );

      if (response?.err) {
        const transformedError = response?.err
          ? typeof response.err === "string"
            ? response.err
            : "An error occurred while assigning the account manager"
          : "An error occurred while assigning the account manager";

        toast({
          title: "Error",
          description: transformedError,
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        return;
      }

      toast({
        title: "Success",
        description: "Account manager assigned successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      const managerDetails = availableManagers.find(
        (manager) => manager.id === Number(selectedManagerID)
      );

      setPartnerData((prevData: any) => {
        return {
          ...prevData,
          accountManagers: [
            ...prevData.accountManagers,
            {
              id: managerDetails?.id,
              mail: managerDetails?.mail,
              firstName: managerDetails?.firstName,
              lastName: managerDetails?.lastName,
            },
          ],
        };
      });
      setSelectedManagerID("");
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setAssigningManager(false);
    }
  }, [
    selectedManagerID,
    partnerId,
    toast,
    availableManagers,
    setPartnerData,
    onClose,
  ]);

  const getAccountManagersList = useCallback(async () => {
    if (partnerDataLoading) return;

    try {
      setLoadingManagers(true);
      const response = await Get("/manage/account-managers");
      if (Array.isArray(response)) {
        const filteredManagers =
          managers && managers.length > 0
            ? response.filter((manager: AccountManager) => {
                const foundManager = managers.find((m) => m.id === manager.id);

                return !foundManager;
              })
            : response;

        setAvailableManagers(filteredManagers);
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setLoadingManagers(false);
    }
  }, [managers, partnerDataLoading]);

  const handleOpenModal = useCallback(() => {
    onOpen();

    if (!loadingManagers && availableManagers.length === 0) {
      toast({
        title: "No available account managers found to assign.",
        status: "info",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [onOpen, loadingManagers, availableManagers.length, toast]);

  useEffect(() => {
    getAccountManagersList();
  }, [getAccountManagersList]);

  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const onDeleteAlertClose = () => setIsDeleteAlertOpen(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [managerToDelete, setManagerToDelete] = useState<AccountManager | null>(
    null
  );

  const handleMarkManagerAsPrimary = useCallback(
    async (managerId: number, isPrimary: boolean) => {
      showGlobalLoading(
        isPrimary
          ? "Marking Account Manager as Primary"
          : "Unmarking Account Manager as Primary"
      );
      try {
        const response = await Put(
          `/manage/partners/${partnerId}/account-managers/${managerId}/association`,
          {
            isPrimary,
          }
        );

        if (response?.err) {
          toast({
            title: "Error",
            description: isPrimary
              ? "An error occurred while marking the account manager as primary."
              : "An error occurred while unmarking the account manager as primary.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          hideGlobalLoading();
          return;
        }

        setPartnerData((prevData: any) => {
          return {
            ...prevData,
            accountManagers: prevData.accountManagers.map(
              (manager: AccountManager) => {
                if (manager.id === managerId) {
                  return {
                    ...manager,
                    isPrimary,
                  };
                }

                return {
                  ...manager,
                  isPrimary: false,
                };
              }
            ),
          };
        });

        toast({
          title: "Success",
          description: isPrimary
            ? "Account manager marked as primary successfully."
            : "Account manager unmarked as primary successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (error) {
        console.error(error);
      } finally {
        hideGlobalLoading();
      }
    },
    [partnerId, setPartnerData, toast]
  );

  const handleMarkManagerAsPrimaryOverride = useCallback(
    async (managerId: number, overridedManagerId: number) => {
      // PUT /manage/partners/:partnerId/account-managers/:managerId/association BODY { isPrimary: true }
      const confirm = await openOverrideDialog();
      if (!confirm) return;

      showGlobalLoading("Overriding Primary Account Manager");

      try {
        const unMarkResponse = await Put(
          `/manage/partners/${partnerId}/account-managers/${overridedManagerId}/association`,
          {
            isPrimary: false,
          }
        );

        if (unMarkResponse?.err) {
          toast({
            title: "Error",
            description:
              "An error occurred while unmarking the old account manager as primary.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          hideGlobalLoading();
          return;
        }

        const markResponse = await Put(
          `/manage/partners/${partnerId}/account-managers/${managerId}/association`,
          {
            isPrimary: true,
          }
        );

        if (markResponse?.err) {
          toast({
            title: "Error",
            description:
              "An error occurred while marking the new account manager as primary.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          hideGlobalLoading();
          return;
        }

        setPartnerData((prevData: any) => {
          return {
            ...prevData,
            accountManagers: prevData.accountManagers.map(
              (manager: AccountManager) => {
                if (manager.id === managerId) {
                  return {
                    ...manager,
                    isPrimary: true,
                  };
                }

                return {
                  ...manager,
                  isPrimary: false,
                };
              }
            ),
          };
        });

        toast({
          title: "Success",
          description: "New account manager marked as primary successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (error) {
        console.error(error);
      } finally {
        hideGlobalLoading();
      }
    },
    [openOverrideDialog, partnerId, setPartnerData, toast]
  );
  const handleDeleteManager = useCallback(async () => {
    if (managerToDelete) {
      showGlobalLoading("Removing Account Manager");
      const response = await Delete(
        `/manage/partners/${partnerId}/account-managers/${managerToDelete.id}/association`
      );

      if (response?.err) {
        toast({
          title: "Error",
          description: "An error occurred while removing the account manager",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        hideGlobalLoading();
        return;
      }

      toast({
        title: "Success",
        description: "Account manager removed successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setPartnerData((prevData: any) => {
        return {
          ...prevData,
          accountManagers: prevData.accountManagers.filter(
            (manager: AccountManager) => manager.mail !== managerToDelete.mail
          ),
        };
      });
      setIsDeleteAlertOpen(false);
      hideGlobalLoading();
    }
  }, [managerToDelete, partnerId, toast, setPartnerData]);

  return (
    <Flex p={4} flexDir={"column"} borderRadius={"16"} backgroundColor="white">
      <Flex justifyContent={"space-between"}>
        <Heading fontSize={"xl"}>Account Managers</Heading>
        <Flex gap={2}>
          {canViewPrimaryStatus && (
            <Button colorScheme={"brand"} variant="ghost" onClick={onEditOpen}>
              Edit
            </Button>
          )}
          {!partnerDataLoading && isAdmin && (
            <Button
              bgColor={"brand.500"}
              _hover={{ backgroundColor: "brand.600" }}
              color="white"
              onClick={handleOpenModal}
            >
              Add Manager
            </Button>
          )}
        </Flex>
      </Flex>
      <Flex direction={"column"} mt={4} gap={2}>
        {partnerDataLoading ? (
          <>
            <Skeleton height="20px" width="25%" />
            <Skeleton height="20px" width="25%" />
            <Skeleton height="20px" width="25%" />
          </>
        ) : managers?.length > 0 ? (
          managers?.map((manager, index) => (
            <Flex key={"am-" + index} alignItems="center" gap={2}>
              <Text fontSize={"md"} fontWeight="500">
                {manager.firstName} {manager.lastName} ({manager.mail})
              </Text>
            </Flex>
          ))
        ) : (
          <Text color={"gray.500"}>
            No account manager found for this partner.
          </Text>
        )}
      </Flex>
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader id="alert-dialog-title">
            Assign an Account Manager
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody gap={4}>
            <Flex gap={2} direction={"column"}>
              {loadingManagers ? (
                <Text>Loading...</Text>
              ) : availableManagers.length === 0 ? (
                <Text>No available account managers found to assign.</Text>
              ) : (
                <Select
                  placeholder="Select an account manager"
                  value={selectedManagerID}
                  onChange={(e) => setSelectedManagerID(e.target.value)}
                >
                  {availableManagers.map((manager, index) => (
                    <option key={"am-" + index} value={manager.id}>
                      {manager.firstName} {manager.lastName} ({manager.mail})
                    </option>
                  ))}
                </Select>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              onClick={handleCancel}
              borderColor="brand.500"
              variant="outline"
              borderRadius={8}
              color="brand.500"
            >
              Cancel
            </Button>
            <Button
              bgColor="brand.500"
              color={"white"}
              borderRadius={8}
              _hover={{ backgroundColor: "brand.600" }}
              onClick={handleAccept}
              autoFocus
              disabled={!selectedManagerID || assigningManager}
              isLoading={assigningManager}
              loadingText="Assigning"
            >
              Add
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isEditOpen}
        onClose={handleCancelEditModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader id="alert-dialog-title">
            Edit Account Managers
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody gap={4}>
            <Flex gap={2} direction={"column"}>
              {managers?.map((manager, index) => (
                <Flex key={"am-" + index} alignItems="center" gap={2}>
                  <Text fontSize={"md"} flex={1} fontWeight="500">
                    {manager.firstName} {manager.lastName} ({manager.mail})
                  </Text>
                  {manager.isPrimary && (
                    <Tag colorScheme={"brand"}>Primary</Tag>
                  )}
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<HiDotsVertical />}
                      aria-label="Remove manager"
                      variant="ghost"
                    />
                    <MenuList>
                      {manager.isPrimary ? (
                        <MenuItem
                          onClick={() =>
                            handleMarkManagerAsPrimary(manager.id, false)
                          }
                        >
                          Unmark as Primary
                        </MenuItem>
                      ) : (
                        <MenuItem
                          onClick={() => {
                            if (!canSelectAnAMAsPrimary) {
                              handleMarkManagerAsPrimaryOverride(
                                manager.id,
                                managers.find((m) => m.isPrimary).id
                              );
                              return;
                            }
                            handleMarkManagerAsPrimary(manager.id, true);
                          }}
                        >
                          Mark as Primary
                        </MenuItem>
                      )}
                      {isAdmin && (
                        <MenuItem
                          onClick={() => {
                            setManagerToDelete(manager);
                            setIsDeleteAlertOpen(true);
                          }}
                          color="red.500"
                        >
                          Remove
                        </MenuItem>
                      )}
                    </MenuList>
                  </Menu>
                </Flex>
              ))}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove Account Manager
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? You can't undo this action afterwards.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteManager} ml={3}>
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      {ConfirmOverrideDialog}
    </Flex>
  );
};

export default AccountManagerView;
